
import React, {useEffect, useState} from 'react';
import { useTranslation } from '../i18n';
import {calculatorAPI, publicApi} from '../api';
import {toast} from "../components/common/Notifier";
import { useAuth } from '../contexts/AuthContext';

export const CalculatorPage = () => {
  const { t } = useTranslation();
  const { athlete, isGuest } = useAuth();

  // === VDOT Калькулятор ===
  const [distance, setDistance] = useState('');
  const [timeMinutes, setTimeMinutes] = useState('');
  const [timeSeconds, setTimeSeconds] = useState('');
  const [vdotInput, setVdotInput] = useState('');
  const [vdotResult, setVdotResult] = useState(null);
  const [vdotLoading, setVdotLoading] = useState(false);
  const [vdotActiveMode, setVdotActiveMode] = useState('race'); // 'race' | 'vdot'

  // === Гостевой калькулятор (гидратация) ===
  const [paceMinutes, setPaceMinutes] = useState('');
  const [paceSeconds, setPaceSeconds] = useState('');
  const [hours, setHours] = useState('');
  const [minutes, setMinutes] = useState('');
  const [weight, setWeight] = useState('');
  const [city, setCity] = useState('');
  const [hydrationResult, setHydrationResult] = useState(null);
  const [hydrationLoading, setHydrationLoading] = useState(false);

  const [activeTab, setActiveTab] = useState('vdot'); // 'vdot' | 'hydration' | 'compare'

  const [comparePlan, setComparePlan] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);

    // Автозаполнение VDOT и веса для авторизованного пользователя
  useEffect(() => {
    if (athlete && !isGuest) {
      // Если есть текущий VDOT - подставляем в поле ввода
      if (athlete.current_vdot) {
        setVdotInput(athlete.current_vdot.toString());
        // Если активна вкладка VDOT и нет результата - можно показать подсказку
      }

      // Если есть вес - подставляем в поле веса для гидратации
      if (athlete.weight) {
        setWeight(athlete.weight.toString());
      }
    }
  }, [athlete, isGuest]);

  //  Правильный useEffect - только один, с правильными зависимостями
  useEffect(() => {
    // Если изменились данные VDOT - сбрасываем comparePlan
    setComparePlan(null);
  }, [vdotResult, vdotInput, distance, timeMinutes, timeSeconds]);

  // === Вспомогательные функции ===


  const formatTime = (seconds) => {
    if (!seconds) return '—';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // === Обработчик генерации планов ===
  const handleGenerateComparePlans = async () => {
    // Берем данные из VDOT калькулятора
    const vdot = Number(vdotResult?.vdot || vdotInput) || 45;
    const weeklyVolume = 60; // или можно дать пользователю ввести

    setCompareLoading(true);
    try {
      // Генерируем оба плана
      const [danielsRes, norwegianRes] = await Promise.all([
        publicApi.post('/generate-weekly-plan/', {
          vdot: vdot,
          weekly_volume_km: weeklyVolume,
          method: 'daniels',
        }),
        publicApi.post('/generate-weekly-plan/', {
          vdot: vdot,
          weekly_volume_km: weeklyVolume,
          method: 'norwegian',
        }),
      ]);

      setComparePlan({
        daniels: danielsRes.data,
        norwegian: norwegianRes.data,
      });
      toast.success(t('compare.plans_generated'));
    } catch (error) {
      toast.error(error.response?.data?.error || t('error.general'));
    } finally {
      setCompareLoading(false);
    }
  };

  // === Рендер сравнения планов ===
  const renderComparePlans = () => {
    if (!comparePlan) {
      return (
        <div className="text-center py-8">
          <p className="text-gray-500 mb-4">{t('compare.click_to_generate')}</p>
          <button
            onClick={handleGenerateComparePlans}
            disabled={compareLoading}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
          >
            {compareLoading ? t('common.loading') : `🚀 ${t('compare.generate')}`}
          </button>
        </div>
      );
    }

    const daniels = comparePlan.daniels;
    const norwegian = comparePlan.norwegian;

    // Группируем тренировки по дням
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    // Создаем карту дней для каждого плана
    const danielsMap = {};
    const norwegianMap = {};

    daniels.workouts.forEach(w => {
      const day = days[w.weekday] || `Day ${w.weekday}`;
      danielsMap[day] = w;
    });

    norwegian.workouts.forEach(w => {
      const day = days[w.weekday] || `Day ${w.weekday}`;
      norwegianMap[day] = w;
    });

      // Подсчет статистики для каждого плана
  const getPlanStats = (workouts) => {
    const total = workouts.length;
    const quality = workouts.filter(w => w.type === 'threshold' || w.type === 'medium_intervals' || w.type === 'short_intervals' || w.type === 'long_intervals').length;
    const easy = workouts.filter(w => w.type === 'easy').length;
    const long = workouts.filter(w => w.type === 'long_run').length;
    const rest = workouts.filter(w => w.type === 'rest').length;
    const totalKm = workouts.reduce((sum, w) => sum + (w.distance_km || 0), 0);

    return { total, quality, easy, long, rest, totalKm: Math.round(totalKm) };
  };

  const danielsStats = getPlanStats(daniels.workouts);
  const norwegianStats = getPlanStats(norwegian.workouts);

  return (
    <div className="space-y-6">
      {/* Три блока  */}
      <div className="grid grid-cols-3 gap-4">
      {/* ✅ Информационное сообщение */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="text-2xl">💡</div>
          <div>
            <p className="font-semibold text-yellow-800">
              {t('compare.demo_notice_title')}
            </p>
            <p className="text-sm text-yellow-700 mt-1">
              {t('compare.demo_notice_text')}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                🌡️ {t('compare.weather_adjustment')}
              </span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                🏃 {t('compare.personalized_paces')}
              </span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                📊 {t('compare.advanced_analytics')}
              </span>
            </div>
            <button
              onClick={() => window.location.href = '/register'}
              className="mt-3 text-sm font-medium text-yellow-800 hover:text-yellow-900 underline"
            >
              {t('compare.register_for_pro')} →
            </button>
          </div>
        </div>
      </div>

        {/* Блок 2: Дэниелс */}
        <div className="bg-blue-50 rounded-lg p-4 text-center border border-blue-200">
          <h3 className="font-bold text-blue-700">📚 {t('compare.daniels')}</h3>
          <p className="text-xs text-gray-500">{t('compare.daniels_desc')}</p>
          <div className="grid grid-cols-3 gap-1 mt-2 text-xs">
            <div>
              <div className="font-bold text-blue-600">{danielsStats.quality}</div>
              <div className="text-gray-500">{t('compare.quality')}</div>
            </div>
            <div>
              <div className="font-bold text-blue-600">{danielsStats.totalKm}</div>
              <div className="text-gray-500">{t('common.km')}</div>
            </div>
            <div>
              <div className="font-bold text-blue-600">{danielsStats.total}</div>
              <div className="text-gray-500">{t('compare.sessions')}</div>
            </div>
          </div>
        </div>

        {/* Блок 3: Норвежский */}
        <div className="bg-green-50 rounded-lg p-4 text-center border border-green-200">
          <h3 className="font-bold text-green-700">🏃 {t('compare.norwegian')}</h3>
          <p className="text-xs text-gray-500">{t('compare.norwegian_desc')}</p>
          <div className="grid grid-cols-3 gap-1 mt-2 text-xs">
            <div>
              <div className="font-bold text-green-600">{norwegianStats.quality}</div>
              <div className="text-gray-500">{t('compare.quality')}</div>
            </div>
            <div>
              <div className="font-bold text-green-600">{norwegianStats.totalKm}</div>
              <div className="text-gray-500">{t('common.km')}</div>
            </div>
            <div>
              <div className="font-bold text-green-600">{norwegianStats.total}</div>
              <div className="text-gray-500">{t('compare.sessions')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Сравнение по дням */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="grid grid-cols-3 gap-px bg-gray-200">
          {/* Заголовки */}
          <div className="bg-gray-50 p-3 font-semibold text-gray-700">
            {t('compare.day')}
          </div>
          <div className="bg-blue-50 p-3 font-semibold text-blue-700 text-center">
            📚 {t('compare.daniels')}
          </div>
          <div className="bg-green-50 p-3 font-semibold text-green-700 text-center">
            🏃 {t('compare.norwegian')}
          </div>

          {/* Дни недели */}
          {days.map(day => {
            const danielsWorkout = danielsMap[day];
            const norwegianWorkout = norwegianMap[day];

            return (
              <React.Fragment key={day}>
                <div className="bg-white p-3 font-medium text-gray-700">
                  {t(`compare.${day.toLowerCase()}`)}
                </div>
                <div className="bg-white p-3 text-sm">
                  {danielsWorkout ? (
                    <div className={danielsWorkout.type === 'rest' ? 'text-gray-400' : ''}>
                      <div className="font-medium text-blue-600">
                        {danielsWorkout.title || danielsWorkout.type}
                      </div>
                      {danielsWorkout.distance_km > 0 && (
                        <div className="text-gray-500 text-xs">
                          {danielsWorkout.distance_km} км
                        </div>
                      )}
                      {danielsWorkout.pace && (
                        <div className="text-xs text-gray-400">
                          {danielsWorkout.pace}
                        </div>
                      )}
                      {danielsWorkout.reps && (
                        <div className="text-xs text-gray-400">
                          {danielsWorkout.reps}×{danielsWorkout.rep_duration}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </div>
                <div className="bg-white p-3 text-sm">
                  {norwegianWorkout ? (
                    <div className={norwegianWorkout.type === 'rest' ? 'text-gray-400' : ''}>
                      <div className="font-medium text-green-600">
                        {norwegianWorkout.title || norwegianWorkout.type}
                      </div>
                      {norwegianWorkout.distance_km > 0 && (
                        <div className="text-gray-500 text-xs">
                          {norwegianWorkout.distance_km} км
                        </div>
                      )}
                      {norwegianWorkout.pace && (
                        <div className="text-xs text-gray-400">
                          {norwegianWorkout.pace}
                        </div>
                      )}
                      {norwegianWorkout.reps && (
                        <div className="text-xs text-gray-400">
                          {norwegianWorkout.reps}×{norwegianWorkout.rep_duration}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Сравнение характеристик */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-semibold text-gray-800 mb-4">
          {t('compare.comparison_table')}
        </h3>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div className="font-medium text-gray-700">{t('compare.feature')}</div>
          <div className="font-medium text-blue-700 text-center">{t('compare.daniels')}</div>
          <div className="font-medium text-green-700 text-center">{t('compare.norwegian')}</div>

          <div className="text-gray-600">{t('compare.quality_sessions')}</div>
          <div className="text-center">2</div>
          <div className="text-center">3</div>

          <div className="text-gray-600">{t('compare.intensity')}</div>
          <div className="text-center">85-100% VO₂max</div>
          <div className="text-center">83-88% VO₂max</div>

          <div className="text-gray-600">{t('compare.recovery_time')}</div>
          <div className="text-center">48-72 ч</div>
          <div className="text-center">48 ч</div>

          <div className="text-gray-600">{t('compare.weekly_volume')}</div>
          <div className="text-center">15-20%</div>
          <div className="text-center">20-25%</div>

          <div className="text-gray-600">{t('compare.risk')}</div>
          <div className="text-center text-red-500">⬆️ {t('compare.higher')}</div>
          <div className="text-center text-green-500">⬇️ {t('compare.lower')}</div>

          <div className="text-gray-600">{t('compare.best_for')}</div>
          <div className="text-center text-sm">{t('compare.daniels_best')}</div>
          <div className="text-center text-sm">{t('compare.norwegian_best')}</div>
        </div>
      </div>
    </div>
  );
};

  // === Обработчики VDOT ===
  const handleCalculateByRace = async () => {
    if (!distance || (!timeMinutes && !timeSeconds)) {
      toast.error(t('vdot.fill_all_fields'));
      return;
    }

    const mins = parseInt(timeMinutes) || 0;
    const secs = parseInt(timeSeconds) || 0;
    const totalMinutes = mins + secs / 60;

    setVdotLoading(true);
    try {
      const { data } = await calculatorAPI.byRace(distance, totalMinutes.toFixed(2));
      setVdotResult(data);
      toast.success(t('vdot.calculated'));
    } catch (error) {
      toast.error(error.response?.data?.error || t('error.general'));
    } finally {
      setVdotLoading(false);
    }
  };

const handleCalculateByVDOT = async () => {
  console.log('🔍 handleCalculateByVDOT called');
  console.log('vdotInput:', vdotInput);

  if (!vdotInput) {
    toast.error(t('vdot.enter_vdot'));
    return;
  }

  setVdotLoading(true);
  try {
    const { data } = await calculatorAPI.byVdot(vdotInput);
    // console.log('✅ Response:', data);
    setVdotResult(data);
    toast.success(t('vdot.calculated'));
  } catch (error) {
    console.error('❌ Error:', error);
    toast.error(error.response?.data?.error || t('error.general'));
  } finally {
    setVdotLoading(false);
  }
};

// === Обработчик гидратации ===
const handleHydrationCalculate = async () => {
  // Собираем темп из двух полей
  const mins = parseInt(paceMinutes) || 0;
  const secs = parseInt(paceSeconds) || 0;

  if (!paceMinutes && !paceSeconds) {
    toast.error(t('guest.fill_pace'));
    return;
  }

  // Собираем длительность из часов и минут
  const h = parseInt(hours) || 0;
  const m = parseInt(minutes) || 0;
  const totalMinutes = h * 60 + m;

  if (totalMinutes < 1) {
    toast.error(t('guest.fill_duration'));
    return;
  }

  if (!weight) {
    toast.error(t('guest.fill_weight'));
    return;
  }

  if (!city) {
    toast.error(t('guest.fill_city'));
    return;
  }

  // Формируем темп в формате "MM:SS"
  const paceStr = `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;

  setHydrationLoading(true);
  try {
    const { data } = await publicApi.get('/hydration/', {
      params: {
        pace: paceStr,
        duration_min: totalMinutes,
        weight_kg: weight,
        city,
        planned_pace: paceStr,
      },
    });
    setHydrationResult(data);
    toast.success(t('guest.calculated'));
  } catch (error) {
    toast.error(error.response?.data?.error || t('error.general'));
  } finally {
    setHydrationLoading(false);
  }
};

// === Рендер результата VDOT ===
const renderVDOTResult = () => {
  if (!vdotResult) return null;

  // Получаем текст комментария
  const getCommentary = () => {
    if (vdotResult.performance_level_key) {
      return t(`vdot_commentary.${vdotResult.performance_level_key}`);
    }
    return vdotResult.performance_level || '—';
  };

  // Дистанции для интервалов
  const intervalDistances = [
    { key: '200m', label: '200m' },
    { key: '300m', label: '300m' },
    { key: '400m', label: '400m' },
    { key: '600m', label: '600m' },
    { key: '800m', label: '800m' },
    { key: '1000m', label: '1000m' },
    { key: '1200m', label: '1200m' },
  ];

  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-6">
      {/* VDOT и уровень */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-200">
        <div>
          <div className="text-sm text-gray-500">{t('vdot.your_vdot')}</div>
          <div className="text-4xl font-bold text-blue-600">
            {vdotResult.vdot || '—'}
          </div>
        </div>
        <div className="text-right">
          <div className="text-sm text-gray-500">{t('vdot.level')}</div>
          <div className="text-lg font-semibold text-green-600">
            {getCommentary()}
          </div>
        </div>
      </div>

      {/* Эквивалентные времена */}
      {vdotResult.equivalent_times && (
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-3">
            🏁 {t('vdot.equivalent_times')}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {Object.entries(vdotResult.equivalent_times).map(([key, value]) => (
              <div key={key} className="bg-gray-50 rounded-lg p-3 text-center">
                <div className="text-xs text-gray-500">
                  {t(`vdot.distance_${key}`, key)}
                </div>
                <div className="font-mono font-medium text-sm">
                  {formatTime(value.time_sec)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Тренировочные пейсы */}
      {vdotResult.training_paces && (
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-3">
            🏃 {t('vdot.training_paces')}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
            {vdotResult.training_paces.easy && (
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <div className="text-xs text-gray-500">{t('vdot.easy')}</div>
                <div className="font-medium text-sm">
                  {vdotResult.training_paces.easy.range_low_str}–{vdotResult.training_paces.easy.range_high_str}
                </div>
              </div>
            )}
            {vdotResult.training_paces.marathon && (
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <div className="text-xs text-gray-500">{t('vdot.marathon')}</div>
                <div className="font-medium text-sm">
                  {vdotResult.training_paces.marathon.pace_str}
                </div>
              </div>
            )}
            {vdotResult.training_paces.threshold && (
              <div className="bg-orange-50 rounded-lg p-3 text-center">
                <div className="text-xs text-gray-500">{t('vdot.threshold')}</div>
                <div className="font-medium text-sm">
                  {vdotResult.training_paces.threshold.pace_str}
                </div>
              </div>
            )}
            {vdotResult.training_paces.interval && (
              <div className="bg-red-50 rounded-lg p-3 text-center">
                <div className="text-xs text-gray-500">{t('vdot.interval')}</div>
                <div className="font-medium text-sm">
                  {vdotResult.training_paces.interval.pace_str}
                </div>
              </div>
            )}
            {vdotResult.training_paces.repetition && (
              <div className="bg-purple-50 rounded-lg p-3 text-center">
                <div className="text-xs text-gray-500">{t('vdot.repetition')}</div>
                <div className="font-medium text-sm">
                  {vdotResult.training_paces.repetition.pace_str}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Интервальные времена - таблица с 200м, 300м, 400м, 600м, 800м, 1000м, 1200м */}
      {vdotResult.interval_times && (
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-3">
            ⏱ {t('vdot.interval_times')}
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('vdot.distance')}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-red-600 uppercase tracking-wider">
                    {t('vdot.interval')}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-purple-600 uppercase tracking-wider">
                    {t('vdot.repetition')}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-indigo-600 uppercase tracking-wider">
                    {t('vdot.fast_reps')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">


                {intervalDistances.map(({ key, label }) => {
                  const intervalTime = vdotResult.interval_times[key];
                  const repTime = vdotResult.interval_times[`${key}_repetition`];
                  const fastRepsTime = vdotResult.interval_times[`${key}_fast_reps`];

                  // Показываем строку, если есть хоть какие-то данные
                  if (!intervalTime && !repTime && !fastRepsTime) return null;

                  return (
                    <tr key={key}>
                      <td className="px-3 py-2 text-sm font-medium text-gray-900">
                        {label}
                      </td>
                      <td className="px-3 py-2 text-sm text-red-600 font-mono">
                        {intervalTime?.display || '—'}
                      </td>
                      <td className="px-3 py-2 text-sm text-purple-600 font-mono">
                        {repTime?.display || '—'}
                      </td>
                      <td className="px-3 py-2 text-sm text-indigo-600 font-mono">
                        {fastRepsTime?.display || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-400 mt-2">
            {t('vdot.interval_times_note')}
          </p>
        </div>
      )}
    </div>
  );
};

  // === Рендер результата гидратации ===
  const renderHydrationResult = () => {
    if (!hydrationResult) return null;

    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-6 space-y-2 text-sm">
        <p className="font-medium">
          🌡 {hydrationResult.weather.temperature}°C, {t('guest.dew_point')} {hydrationResult.weather.dew_point}°C
        </p>

        {hydrationResult.weather.city && (
          <p className="text-gray-500">📍 {hydrationResult.weather.city}, {hydrationResult.weather.country}</p>
        )}

        {hydrationResult.pace && (
          <p className="font-medium text-blue-600">
            🏃 {t('guest.pace_adjustment')}: {hydrationResult.pace.base_pace} → {hydrationResult.pace.target_pace}
            <span className="text-sm text-gray-500 ml-1">(+{hydrationResult.pace.penalty_pct}%)</span>
          </p>
        )}

        <div className="border-t border-gray-200 my-2"></div>

        <p>💧 {t('guest.fluid_loss')}: ~{hydrationResult.hydration.fluid_needed_ml} {t('guest.ml')}</p>
        <p>📊 {t('guest.sweat_rate')}: {hydrationResult.hydration.sweat_rate_ml_per_hour} {t('guest.ml_per_hour')}</p>

        {hydrationResult.hydration.carry_ml_range ? (
          <p>🎒 {t('guest.carry')}: {hydrationResult.hydration.carry_ml_range[0]}-{hydrationResult.hydration.carry_ml_range[1]} {t('guest.ml')}</p>
        ) : (
          <p className="text-gray-500">🎒 {t('guest.carry_not_needed')}</p>
        )}

        <p>🚰 {t('guest.after')}: {hydrationResult.hydration.post_workout_ml_range[0]}-{hydrationResult.hydration.post_workout_ml_range[1]} {t('guest.ml')}</p>

        <p className="text-xs text-gray-500 mt-2">
          {hydrationResult.hydration.weight_was_provided
            ? `✅ ${t('guest.weight_used')}: ${hydrationResult.hydration.weight_used_kg} ${t('common.kg')}`
            : `⚠️ ${t('guest.weight_default')} (${hydrationResult.hydration.weight_used_kg} ${t('common.kg')})`}
        </p>
      </div>
    );
  };

return (
  <div className="max-w-4xl mx-auto py-8 px-4">
    <div className="flex items-center gap-3 mb-2">
      <img
        src="/moom1.png"
        alt="Moomi the Troll"
        className="w-10 h-10 rounded-full object-cover border-2 border-blue-200"
      />
      <h1 className="text-3xl font-bold text-gray-800">
        {t('calculator.title')}
      </h1>
    </div>
    <p className="text-gray-600 mb-6">{t('calculator.subtitle')}</p>

            {/* Если пользователь авторизован - показываем его VDOT */}
      {athlete && !isGuest && athlete.current_vdot && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-blue-600 text-lg">📊</span>
            <span className="text-sm text-blue-800">
              {t('calculator.your_vdot')}: <strong>{athlete.current_vdot}</strong>
            </span>
            <span className="text-xs text-blue-600 ml-2">
              {t('calculator.auto_filled')}
            </span>
          </div>
          <button
            onClick={() => {
              setVdotInput(athlete.current_vdot.toString());
              toast.info(t('calculator.vdot_restored'));
            }}
            className="text-xs text-blue-600 hover:text-blue-800 underline"
          >
            {t('calculator.restore')}
          </button>
        </div>
      )}

      {/* Если есть вес - показываем в гидратации */}
      {athlete && !isGuest && athlete.weight && activeTab === 'hydration' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-green-600 text-lg">⚖️</span>
            <span className="text-sm text-green-800">
              {t('calculator.your_weight')}: <strong>{athlete.weight} {t('common.kg')}</strong>
            </span>
            <span className="text-xs text-green-600 ml-2">
              {t('calculator.auto_filled')}
            </span>
          </div>
        </div>
      )}

    {/* Вкладки */}
    <div className="flex border-b border-gray-200 mb-6 overflow-x-auto">
      <button
        className={`px-6 py-3 font-medium text-sm transition-colors whitespace-nowrap ${
          activeTab === 'vdot'
            ? 'border-b-2 border-blue-600 text-blue-600'
            : 'text-gray-500 hover:text-gray-700'
        }`}
        onClick={() => setActiveTab('vdot')}
      >
        🏃 {t('calculator.tab_vdot')}
      </button>
        {/* ДОБАВЛЯЕМ ТРЕТЬЮ ВКЛАДКУ */}
      <button
        className={`px-6 py-3 font-medium text-sm transition-colors whitespace-nowrap ${
          activeTab === 'compare'
            ? 'border-b-2 border-blue-600 text-blue-600'
            : 'text-gray-500 hover:text-gray-700'
        }`}
        onClick={() => setActiveTab('compare')}
      >
        📊 {t('calculator.tab_compare')}
      </button>
      <button
        className={`px-6 py-3 font-medium text-sm transition-colors whitespace-nowrap ${
          activeTab === 'hydration'
            ? 'border-b-2 border-blue-600 text-blue-600'
            : 'text-gray-500 hover:text-gray-700'
        }`}
        onClick={() => setActiveTab('hydration')}
      >
        💧 {t('calculator.tab_hydration')}
      </button>
    </div>

    {/* Контент вкладок */}
    {activeTab === 'vdot' ? (
      // === Вкладка VDOT ===
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          {/* Подвкладки VDOT */}
          <div className="flex border-b border-gray-200 mb-4">
            <button
              className={`px-4 py-2 text-sm font-medium ${
                vdotActiveMode === 'race'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => {
                setVdotActiveMode('race');
                // Очищаем результат при переключении
                setVdotResult(null);
                setDistance('');
                setTimeMinutes('');
                setTimeSeconds('');
              }}
            >
              🏁 {t('vdot.tab_race')}
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium ${
                vdotActiveMode === 'vdot'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => {
                setVdotActiveMode('vdot');
                // Очищаем результат при переключении
                setVdotResult(null);
                setVdotInput('');
              }}
            >
              📊 {t('vdot.tab_vdot')}
            </button>
          </div>

          {/* Форма в зависимости от активной подвкладки */}
          {vdotActiveMode === 'race' ? (
            // Форма по забегу
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  📏 {t('vdot.distance')}
                </label>
                <input
                  type="number"
                  value={distance}
                  onChange={(e) => setDistance(e.target.value)}
                  placeholder={t('vdot.distance_placeholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ⏱ {t('vdot.minutes')}
                  </label>
                  <input
                    type="number"
                    value={timeMinutes}
                    onChange={(e) => setTimeMinutes(e.target.value)}
                    placeholder="20"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ⏱ {t('vdot.seconds')}
                  </label>
                  <input
                    type="number"
                    value={timeSeconds}
                    onChange={(e) => setTimeSeconds(e.target.value)}
                    placeholder="30"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <button
                onClick={handleCalculateByRace}
                disabled={vdotLoading}
                className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 transition-all"
              >
                {vdotLoading ? t('common.loading') : `🚀 ${t('vdot.calculate')}`}
              </button>
            </div>
          ) : (
            // Форма по VDOT
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  📊 {t('vdot.vdot_value')}
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={vdotInput}
                  onChange={(e) => setVdotInput(e.target.value)}
                  placeholder={t('vdot.vdot_placeholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <button
                onClick={handleCalculateByVDOT}
                disabled={vdotLoading}
                className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 transition-all"
              >
                {vdotLoading ? t('common.loading') : `🚀 ${t('vdot.calculate')}`}
              </button>
            </div>
          )}
        </div>
              {/* Результат */}
        {renderVDOTResult()}
      </div>

    ) : activeTab === 'hydration' ? (

        // Вкладка Гидратация

        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            {/* Город */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                📍 {t('guest.city_label')}
              </label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder={t('guest.city_placeholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Темп - два поля */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                🏃 {t('guest.pace_label')}
              </label>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={paceMinutes}
                    onChange={(e) => setPaceMinutes(e.target.value)}
                    placeholder={t('guest.pace_min_placeholder')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <span className="text-gray-500 text-lg font-bold">:</span>
                <div className="flex-1">
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={paceSeconds}
                    onChange={(e) => setPaceSeconds(e.target.value)}
                    placeholder={t('guest.pace_sec_placeholder')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <span className="text-gray-500 text-sm ml-1">{t('guest.pace_per_km')}</span>
              </div>
            </div>

            {/* Длительность - часы и минуты */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ⏱ {t('guest.duration_label')}
              </label>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <input
                    type="number"
                    min="0"
                    value={hours}
                    onChange={(e) => setHours(e.target.value)}
                    placeholder={t('guest.hours_placeholder')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <span className="text-gray-500 text-lg font-bold">:</span>
                <div className="flex-1">
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={minutes}
                    onChange={(e) => setMinutes(e.target.value)}
                    placeholder={t('guest.minutes_placeholder')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <span className="text-gray-500 text-sm ml-1">{t('guest.hours_minutes')}</span>
              </div>
            </div>

            {/* Вес */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ⚖️ {t('guest.weight_label')}
              </label>
              <input
                type="number"
                step="0.1"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder={t('guest.weight_placeholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <button
              onClick={handleHydrationCalculate}
              disabled={hydrationLoading}
              className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 transition-all"
            >
              {hydrationLoading ? t('common.loading') : `🚀 ${t('guest.calculate')}`}
            </button>
          </div>

    {renderHydrationResult()}
  </div>
  ) : (
    // === Вкладка Сравнение ===
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          📊 {t('compare.title')}
        </h2>
        <p className="text-gray-600 mb-4">{t('compare.subtitle')}</p>

        {renderComparePlans()}
      </div>
    </div>
  )}
    </div>
  );
};
